import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { db } from 'db/db-dexie';
import { useAuth } from './AuthContext';

// Services reales del sistema
import AccountService from 'services/Contabilidad/AccountService';
import SubAccountService from 'services/Contabilidad/SubAccountService';
import FunctionsService from 'services/Contabilidad/FunctionsService';
import FundService from 'services/Contabilidad/FundService';
import ImpuestoTarifa from 'services/Impuesto/ImpuestoTarifaService';
import SustentoTributarioComprobanteTipo from 'services/SustentoTributarioComprobanteTipo/SustentoTributarioComprobanteTipoService';
import ProductoTipo from 'services/ProductoTipo/ProductoTipoService';
import PuntoEmision from 'services/PuntoEmision/PuntoEmisionService';
import Pais from 'services/Pais/PaisService';
import ParaisoFiscal from 'services/Pais/ParaisoFiscalService';
import ImpuestoRetenerTarifa from 'services/ImpuestoRetenerTarifa/ImpuestoRetenerTarifaService';
import ImpuestoRetener from 'services/ImpuestoRetenerTarifa/ImpuestoRetenerService';
import FormaPagoSri from 'services/FormaPagoSri/FormaPagoSriService';
import ComprobanteTipo from 'services/ComprobanteTipo/ComprobanteTipoService';
import Sucursal from 'services/Sucursal/SucursalService';
import ContribuyenteTipo from 'services/Contribuyente/ContribuyenteService';
import ProveedorTipo from 'services/Proveedor/ProveedorTipoService';
import IdentificacionTipo from 'services/Identificacion/IdentificacionService';
import Canton from 'services/Pais/CantonService';
import ParteRelacionada from 'services/ParteRelacionada/ParteRelacionadaService';
import UafeService from 'services/UAFE/UafeService';
import PagoTipo from 'services/Pais/PagoTipoService';
import RegimenFiscalExteriorTipo from 'services/Pais/RegimenFiscalExteriorTipoService';
import Ciudad from 'services/Ciudad/CiudadService';
import ConfigRegimenFiscalExteriorTipo from 'services/Config/ConfigRegimenFiscalExteriorTipoService';
import ConfigPais from 'services/Config/ConfigPaisService';
import ConfigIdentificacionTipo from 'services/Config/ConfigIdentificacionTipoService';
import ConfigProveedorTipo from 'services/Config/ConfigProveedorTipoService';
import ConfigImpuestoTarifa from 'services/Config/ConfigImpuestoTarifaService';
import AmbientePuntoEmision from 'services/AmbientePuntoEmision/AmbientePuntoEmisionService';

// Clave segura para metadata no sensible en localStorage
const CONFIG_META_KEY = '_config_monitor_meta';

// Helper para leer metadata persistida
const getStoredMetadata = () => {
    try {
        const raw = localStorage.getItem(CONFIG_META_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

// Helper para guardar metadata de un item
const updateStoredMetadata = (key, update) => {
    try {
        const current = getStoredMetadata();
        current[key] = { ...(current[key] || {}), ...update };
        localStorage.setItem(CONFIG_META_KEY, JSON.stringify(current));
    } catch (e) {
        console.warn('Error guardando metadata de configuracion:', e);
    }
};

// ============================================================================
// INVENTARIO REAL DE CONFIGURACIONES Y CARGAS INICIALES
// ============================================================================
export const CATALOG_REGISTRY = [
    // --- CRÍTICOS INDEXEDDB ---
    {
        key: 'account',
        label: 'Plan de Cuentas',
        storageType: 'IndexedDB',
        storeOrKey: 'account',
        isCritical: true,
        service: 'AccountService.List()',
        modules: 'Cuentas contables, Asiento gasto/pago, Facturas, Registro compra, Vale caja',
        fetchFn: async () => await AccountService.List(),
        countFn: async () => {
            try {
                return await db.account.count();
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'subAccount',
        label: 'Subcuentas',
        storageType: 'IndexedDB',
        storeOrKey: 'subAccount',
        isCritical: true,
        service: 'SubAccountService.GetAll()',
        modules: 'Asientos contables, Forma de pago, Registro compra, Vale caja',
        fetchFn: async () => await SubAccountService.GetAll(),
        countFn: async () => {
            try {
                return await db.subAccount.count();
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'functions',
        label: 'Funciones Contables',
        storageType: 'IndexedDB',
        storeOrKey: 'functions',
        isCritical: true,
        service: 'FunctionsService.List()',
        modules: 'Asientos contables, Transferencias banco',
        skipIf: () => Number(localStorage.getItem('codigoRolTipo')) === 10,
        fetchFn: async () => await FunctionsService.List(),
        countFn: async () => {
            try {
                return await db.functions.count();
            } catch {
                return 0;
            }
        }
    },

    // --- CRÍTICOS LOCALSTORAGE ---
    {
        key: 'listaImpuestoTarifa',
        label: 'Tarifas de Impuesto',
        storageType: 'localStorage',
        storeOrKey: 'listaImpuestoTarifa',
        isCritical: true,
        service: 'ImpuestoTarifa.Get()',
        modules: 'Registro compra, Facturación, Liquidación, Retenciones',
        fetchFn: async () => {
            const res = await ImpuestoTarifa.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaImpuestoTarifa', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Respuesta no válida del servicio ImpuestoTarifa');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaImpuestoTarifa'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaSustentoTributarioComprobanteTipo',
        label: 'Sustento Tributario / Comprobante',
        storageType: 'localStorage',
        storeOrKey: 'listaSustentoTributarioComprobanteTipo',
        isCritical: true,
        service: 'SustentoTributarioComprobanteTipo.Get()',
        modules: 'Registro compra, Liquidaciones, Notas de crédito, ATS',
        fetchFn: async () => {
            const res = await SustentoTributarioComprobanteTipo.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaSustentoTributarioComprobanteTipo', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Respuesta no válida de SustentoTributarioComprobanteTipo');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaSustentoTributarioComprobanteTipo'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaImpuesto',
        label: 'Tarifas de Retención (Impuesto)',
        storageType: 'localStorage',
        storeOrKey: 'listaImpuesto',
        isCritical: true,
        service: 'ImpuestoRetenerTarifa.Get()',
        modules: 'Emisión de retenciones IVA y Renta',
        fetchFn: async () => {
            const res = await ImpuestoRetenerTarifa.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaImpuesto', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Respuesta no válida de ImpuestoRetenerTarifa');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaImpuesto'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaImpuestoRetener',
        label: 'Impuestos a Retener (Códigos SRI)',
        storageType: 'localStorage',
        storeOrKey: 'listaImpuestoRetener',
        isCritical: true,
        service: 'ImpuestoRetener.Get()',
        modules: 'Retenciones electrónicas, códigos tributarios',
        fetchFn: async () => {
            const res = await ImpuestoRetener.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaImpuestoRetener', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Respuesta no válida de ImpuestoRetener');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaImpuestoRetener'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaConfiguracionRetencionIvaContribuyenteTipo',
        label: 'Config. Retención IVA por Contribuyente',
        storageType: 'localStorage',
        storeOrKey: 'listaConfiguracionRetencionIvaContribuyenteTipo',
        isCritical: true,
        service: 'ImpuestoRetenerTarifa.GetConfigIva()',
        modules: 'Cálculo automático de retenciones SRI según tipo de contribuyente',
        fetchFn: async () => {
            const res = await ImpuestoRetenerTarifa.GetConfigIva();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaConfiguracionRetencionIvaContribuyenteTipo', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Respuesta no válida de GetConfigIva');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaConfiguracionRetencionIvaContribuyenteTipo'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaFormaPagoSri',
        label: 'Formas de Pago SRI',
        storageType: 'localStorage',
        storeOrKey: 'listaFormaPagoSri',
        isCritical: true,
        service: 'FormaPagoSri.Get()',
        modules: 'Comprobantes electrónicos, Pago de compras',
        fetchFn: async () => {
            const res = await FormaPagoSri.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaFormaPagoSri', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Respuesta no válida de FormaPagoSri');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaFormaPagoSri'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaComprobanteTipo',
        label: 'Tipos de Comprobante',
        storageType: 'localStorage',
        storeOrKey: 'listaComprobanteTipo',
        isCritical: true,
        service: 'ComprobanteTipo.Get()',
        modules: 'Facturas, Liquidaciones, Notas de venta/crédito',
        fetchFn: async () => {
            const res = await ComprobanteTipo.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaComprobanteTipo', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Respuesta no válida de ComprobanteTipo');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaComprobanteTipo'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaContribuyenteTipo',
        label: 'Tipos de Contribuyente',
        storageType: 'localStorage',
        storeOrKey: 'listaContribuyenteTipo',
        isCritical: true,
        service: 'ContribuyenteTipo.Get()',
        modules: 'Ficha de proveedor, Retenciones, Validaciones SRI',
        fetchFn: async () => {
            const res = await ContribuyenteTipo.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaContribuyenteTipo', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Respuesta no válida de ContribuyenteTipo');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaContribuyenteTipo'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaIdentificacionTipo',
        label: 'Tipos de Identificación',
        storageType: 'localStorage',
        storeOrKey: 'listaIdentificacionTipo',
        isCritical: true,
        service: 'IdentificacionTipo.Get()',
        modules: 'Proveedores, RUC/Cédula/Pasaporte, Registro compra',
        fetchFn: async () => {
            const res = await IdentificacionTipo.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaIdentificacionTipo', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Respuesta no válida de IdentificacionTipo');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaIdentificacionTipo'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },

    // --- SECUNDARIOS LOCALSTORAGE ---
    {
        key: 'listaFund',
        label: 'Fondos (Fund)',
        storageType: 'localStorage',
        storeOrKey: 'listaFund',
        isCritical: false,
        service: 'FundService.Get()',
        modules: 'Contabilidad de fondos',
        fetchFn: async () => {
            const res = await FundService.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaFund', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en FundService');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaFund'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaAccountFormaPago',
        label: 'Cuentas Forma de Pago',
        storageType: 'localStorage',
        storeOrKey: 'listaAccountFormaPago',
        isCritical: false,
        service: 'AccountService.FormaPagoGet()',
        modules: 'Tesorería, Formas de pago bancarias',
        fetchFn: async () => {
            const res = await AccountService.FormaPagoGet();
            if (res && res.code === '1') {
                localStorage.setItem('listaAccountFormaPago', JSON.stringify(JSON.parse(res.payload || '[]')));
                return true;
            }
            localStorage.setItem('listaAccountFormaPago', JSON.stringify([]));
            return true;
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaAccountFormaPago'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaProductoTipo',
        label: 'Tipos de Producto',
        storageType: 'localStorage',
        storeOrKey: 'listaProductoTipo',
        isCritical: false,
        service: 'ProductoTipo.Get()',
        modules: 'Bienes y servicios en compra',
        fetchFn: async () => {
            const res = await ProductoTipo.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaProductoTipo', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en ProductoTipo');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaProductoTipo'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaPuntoEmision',
        label: 'Puntos de Emisión',
        storageType: 'localStorage',
        storeOrKey: 'listaPuntoEmision',
        isCritical: false,
        service: 'PuntoEmision.Get()',
        modules: 'Emisión de comprobantes y retenciones',
        fetchFn: async () => {
            const res = await PuntoEmision.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaPuntoEmision', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en PuntoEmision');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaPuntoEmision'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaPais',
        label: 'Catálogo de Países',
        storageType: 'localStorage',
        storeOrKey: 'listaPais',
        isCritical: false,
        service: 'Pais.Get()',
        modules: 'Proveedores del exterior',
        fetchFn: async () => {
            const res = await Pais.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaPais', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en Pais');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaPais'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaParaisoFiscal',
        label: 'Paraísos Fiscales',
        storageType: 'localStorage',
        storeOrKey: 'listaParaisoFiscal',
        isCritical: false,
        service: 'ParaisoFiscal.Get()',
        modules: 'Pagos al exterior, normativa SRI',
        fetchFn: async () => {
            const res = await ParaisoFiscal.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaParaisoFiscal', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en ParaisoFiscal');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaParaisoFiscal'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaSucursal',
        label: 'Sucursales',
        storageType: 'localStorage',
        storeOrKey: 'listaSucursal',
        isCritical: false,
        service: 'Sucursal.Get()',
        modules: 'Filtros y reportes por sucursal',
        fetchFn: async () => {
            const res = await Sucursal.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaSucursal', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en Sucursal');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaSucursal'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaProveedorTipo',
        label: 'Tipos de Proveedor',
        storageType: 'localStorage',
        storeOrKey: 'listaProveedorTipo',
        isCritical: false,
        service: 'ProveedorTipo.Get()',
        modules: 'Clasificación de proveedores',
        fetchFn: async () => {
            const res = await ProveedorTipo.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaProveedorTipo', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en ProveedorTipo');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaProveedorTipo'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaCanton',
        label: 'Cantones',
        storageType: 'localStorage',
        storeOrKey: 'listaCanton',
        isCritical: false,
        service: 'Canton.Get()',
        modules: 'Direcciones y establecimientos',
        fetchFn: async () => {
            const res = await Canton.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaCanton', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en Canton');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaCanton'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaParteRelacionada',
        label: 'Partes Relacionadas',
        storageType: 'localStorage',
        storeOrKey: 'listaParteRelacionada',
        isCritical: false,
        service: 'ParteRelacionada.Get()',
        modules: 'Ficha de proveedor, Anexos tributarios',
        fetchFn: async () => {
            const res = await ParteRelacionada.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaParteRelacionada', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en ParteRelacionada');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaParteRelacionada'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaRiesgoProveedor',
        label: 'Catálogo de Riesgo Proveedor (UAFE)',
        storageType: 'localStorage',
        storeOrKey: 'listaRiesgoProveedor',
        isCritical: false,
        service: 'UafeService.GetRiesgoCatalogo()',
        modules: 'UAFE Debida Diligencia, Matriz de riesgo',
        fetchFn: async () => {
            const res = await UafeService.GetRiesgoCatalogo();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaRiesgoProveedor', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en UafeService');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaRiesgoProveedor'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaPagoTipo',
        label: 'Tipos de Pago Exterior',
        storageType: 'localStorage',
        storeOrKey: 'listaPagoTipo',
        isCritical: false,
        service: 'PagoTipo.Get()',
        modules: 'Pagos residentes y no residentes',
        fetchFn: async () => {
            const res = await PagoTipo.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaPagoTipo', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en PagoTipo');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaPagoTipo'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaRegimenFiscal',
        label: 'Régimen Fiscal Exterior',
        storageType: 'localStorage',
        storeOrKey: 'listaRegimenFiscal',
        isCritical: false,
        service: 'RegimenFiscalExteriorTipo.Get()',
        modules: 'Comprobantes del exterior',
        fetchFn: async () => {
            const res = await RegimenFiscalExteriorTipo.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaRegimenFiscal', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en RegimenFiscalExteriorTipo');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaRegimenFiscal'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaCiudad',
        label: 'Ciudades',
        storageType: 'localStorage',
        storeOrKey: 'listaCiudad',
        isCritical: false,
        service: 'Ciudad.Get()',
        modules: 'Proveedores, Iglesias, Ubicaciones',
        fetchFn: async () => {
            const res = await Ciudad.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaCiudad', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en Ciudad');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaCiudad'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'ConfigListaRegimenFiscal',
        label: 'Config. Régimen Fiscal Exterior',
        storageType: 'localStorage',
        storeOrKey: 'ConfigListaRegimenFiscal',
        isCritical: false,
        service: 'ConfigRegimenFiscalExteriorTipo.Get()',
        modules: 'Módulo de configuración general',
        fetchFn: async () => {
            const res = await ConfigRegimenFiscalExteriorTipo.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('ConfigListaRegimenFiscal', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en ConfigRegimenFiscalExteriorTipo');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('ConfigListaRegimenFiscal'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'ConfigListaPais',
        label: 'Config. Países',
        storageType: 'localStorage',
        storeOrKey: 'ConfigListaPais',
        isCritical: false,
        service: 'ConfigPais.Get()',
        modules: 'Módulo de configuración general',
        fetchFn: async () => {
            const res = await ConfigPais.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('ConfigListaPais', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en ConfigPais');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('ConfigListaPais'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'ConfigListaIdentificacionTipo',
        label: 'Config. Tipos de Identificación',
        storageType: 'localStorage',
        storeOrKey: 'ConfigListaIdentificacionTipo',
        isCritical: false,
        service: 'ConfigIdentificacionTipo.Get()',
        modules: 'Módulo de configuración general',
        fetchFn: async () => {
            const res = await ConfigIdentificacionTipo.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('ConfigListaIdentificacionTipo', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en ConfigIdentificacionTipo');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('ConfigListaIdentificacionTipo'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'ConfigListaProveedorTipo',
        label: 'Config. Tipos de Proveedor',
        storageType: 'localStorage',
        storeOrKey: 'ConfigListaProveedorTipo',
        isCritical: false,
        service: 'ConfigProveedorTipo.Get()',
        modules: 'Módulo de configuración general',
        fetchFn: async () => {
            const res = await ConfigProveedorTipo.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('ConfigListaProveedorTipo', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en ConfigProveedorTipo');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('ConfigListaProveedorTipo'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'ConfigListaImpuestoTarifa',
        label: 'Config. Tarifas de Impuesto',
        storageType: 'localStorage',
        storeOrKey: 'ConfigListaImpuestoTarifa',
        isCritical: false,
        service: 'ConfigImpuestoTarifa.Get()',
        modules: 'Módulo de configuración general',
        fetchFn: async () => {
            const res = await ConfigImpuestoTarifa.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('ConfigListaImpuestoTarifa', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en ConfigImpuestoTarifa');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('ConfigListaImpuestoTarifa'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    },
    {
        key: 'listaAmbientePuntoEmision',
        label: 'Ambientes Puntos de Emisión',
        storageType: 'localStorage',
        storeOrKey: 'listaAmbientePuntoEmision',
        isCritical: false,
        service: 'AmbientePuntoEmision.Get()',
        modules: 'Puntos de emisión y ambientes',
        fetchFn: async () => {
            const res = await AmbientePuntoEmision.Get();
            if (res && res.code === '1' && res.payload) {
                localStorage.setItem('listaAmbientePuntoEmision', JSON.stringify(JSON.parse(res.payload)));
                return true;
            }
            throw new Error(res?.message || 'Error en AmbientePuntoEmision');
        },
        countFn: async () => {
            try {
                const data = JSON.parse(localStorage.getItem('listaAmbientePuntoEmision'));
                return Array.isArray(data) ? data.length : data ? 1 : 0;
            } catch {
                return 0;
            }
        }
    }
];

// Helper para normalizar mensajes de error amigables
const getFriendlyErrorMessage = (err) => {
    if (!err) return 'Error desconocido al consultar el servicio.';
    const msg = typeof err === 'string' ? err : err.message || '';
    if (msg.includes('Failed to fetch') || msg.includes('network') || err.name === 'TypeError') {
        return 'No fue posible conectar con el servicio (red ocupada o sin conexión).';
    }
    if (msg.includes('401') || msg.includes('Unauthorized')) {
        return 'Sesión no autorizada o expirada.';
    }
    if (msg.includes('403') || msg.includes('Forbidden')) {
        return 'Acceso denegado para el rol actual.';
    }
    if (msg.includes('500') || msg.includes('Internal Server')) {
        return 'El servidor respondió con error interno temporal.';
    }
    return msg.length > 90 ? msg.substring(0, 90) + '...' : msg;
};

// ============================================================================
// CONTEXT
// ============================================================================
const ConfigMonitorContext = createContext();

export const ConfigMonitorProvider = ({ children }) => {
    const { isAuthenticated, isInitializing } = useAuth();

    // Estado por cada configuración
    const [itemStates, setItemStates] = useState(() => {
        const initial = {};
        const meta = getStoredMetadata();
        CATALOG_REGISTRY.forEach((item) => {
            const itemMeta = meta[item.key] || {};
            initial[item.key] = {
                status: 'PENDING', // PENDING | LOADING | READY | ERROR
                recordCount: typeof itemMeta.recordCount === 'number' ? itemMeta.recordCount : 0,
                lastSuccessAt: itemMeta.lastSuccessAt || null,
                lastAttemptAt: null,
                error: null
            };
        });
        return initial;
    });

    const [isSyncingAll, setIsSyncingAll] = useState(false);
    const [isReconciled, setIsReconciled] = useState(false);

    // Registro de promesas en vuelo para DEDUPLICACIÓN
    const inFlightPromises = useRef({});

    // Control de Idempotencia, Sesión y Mutex
    const lastBootstrapSessionKey = useRef(null);
    const currentSessionEpoch = useRef(0);
    const isSyncingAllRef = useRef(false);

    // 0️⃣ Verificación rigurosa de Sesión Lista para bootstrap
    const isSessionReady = useCallback(() => {
        if (isInitializing || !isAuthenticated) return false;

        const jwt = localStorage.getItem('JWT');
        const idSucursal = localStorage.getItem('idSucursal');
        const idRol = localStorage.getItem('idRol');
        const idUsuario = localStorage.getItem('idUsuario');

        if (!jwt || jwt === 'null' || jwt === 'undefined') return false;
        if (!idSucursal || idSucursal === 'null' || idSucursal === 'undefined') return false;
        if (!idRol || idRol === 'null' || idRol === 'undefined') return false;
        if (!idUsuario || idUsuario === 'null' || idUsuario === 'undefined') return false;

        return true;
    }, [isAuthenticated, isInitializing]);

    // 1️⃣ Reconciliar estado con el almacenamiento local real (IndexedDB y localStorage)
    // Sin hacer peticiones HTTP innecesarias.
    const reconcileLocalState = useCallback(async () => {
        const meta = getStoredMetadata();
        const updates = {};

        for (const item of CATALOG_REGISTRY) {
            try {
                if (item.skipIf && item.skipIf()) {
                    updates[item.key] = {
                        status: 'READY',
                        recordCount: 0,
                        lastSuccessAt: meta[item.key]?.lastSuccessAt || null,
                        lastAttemptAt: null,
                        error: null,
                        skipped: true
                    };
                    continue;
                }

                const count = await item.countFn();
                const itemMeta = meta[item.key] || {};
                const hasLocalData = count > 0;

                updates[item.key] = {
                    hasLocalData,
                    count,
                    lastSuccessAt: itemMeta.lastSuccessAt || null
                };
            } catch (err) {
                console.warn(`Error reconciliando ${item.key}:`, err);
            }
        }

        setItemStates((prev) => {
            const next = { ...prev };
            for (const [key, val] of Object.entries(updates)) {
                if (val.skipped) {
                    next[key] = val;
                } else {
                    next[key] = {
                        status: val.hasLocalData ? 'READY' : (prev[key]?.status === 'ERROR' ? 'ERROR' : 'PENDING'),
                        recordCount: val.count,
                        lastSuccessAt: val.lastSuccessAt,
                        lastAttemptAt: prev[key]?.lastAttemptAt || null,
                        error: prev[key]?.error || null
                    };
                }
            }
            return next;
        });
        setIsReconciled(true);
    }, []);

    // 2️⃣ Sincronizar un único item con deduplicación, preservación de caché y epoch guard
    const syncItem = useCallback(async (key, options = {}) => {
        const { forceRefresh = false, retrying = false, sessionEpoch } = options;
        const item = CATALOG_REGISTRY.find((c) => c.key === key);
        if (!item) return false;

        // Si la sesión cambió antes de empezar, descartar
        if (sessionEpoch !== undefined && currentSessionEpoch.current !== sessionEpoch) {
            return false;
        }

        if (item.skipIf && item.skipIf()) {
            setItemStates((prev) => ({
                ...prev,
                [key]: {
                    ...(prev[key] || {}),
                    status: 'READY',
                    error: null,
                    skipped: true
                }
            }));
            return true;
        }

        // Deduplicación: si ya hay una petición en vuelo para este item, devolverla
        if (inFlightPromises.current[key]) {
            return inFlightPromises.current[key];
        }

        // Si no es forceRefresh y ya tiene datos locales válidos, no forzar recarga
        if (!forceRefresh && !retrying) {
            const currentCount = await item.countFn();
            if (currentCount > 0) {
                setItemStates((prev) => ({
                    ...prev,
                    [key]: {
                        ...(prev[key] || {}),
                        status: 'READY',
                        recordCount: currentCount,
                        error: null
                    }
                }));
                return true;
            }
        }

        const thisSessionEpoch = sessionEpoch !== undefined ? sessionEpoch : currentSessionEpoch.current;
        const now = new Date().toISOString();

        // Actualizar a LOADING
        setItemStates((prev) => ({
            ...prev,
            [key]: {
                ...(prev[key] || {}),
                status: 'LOADING',
                lastAttemptAt: now,
                error: null
            }
        }));

        const promise = (async () => {
            try {
                // Ejecución de la llamada HTTP
                await item.fetchFn();

                // Verificación anti-race: si la sesión cambió durante la petición HTTP, descartar
                if (currentSessionEpoch.current !== thisSessionEpoch) {
                    return false;
                }

                // Verificar el conteo real tras la escritura
                const newCount = await item.countFn();
                const successTimestamp = new Date().toISOString();

                // Persistir metadata mínima
                updateStoredMetadata(key, {
                    lastSuccessAt: successTimestamp,
                    recordCount: newCount
                });

                // Si es account o subAccount, asegurar cachedSucursalId
                const currentSucursalId = Number(localStorage.getItem('idSucursal'));
                if (currentSucursalId && (key === 'account' || key === 'subAccount')) {
                    localStorage.setItem('cachedSucursalId', currentSucursalId);
                }

                setItemStates((prev) => {
                    if (currentSessionEpoch.current !== thisSessionEpoch) return prev;
                    return {
                        ...prev,
                        [key]: {
                            status: 'READY',
                            recordCount: newCount,
                            lastSuccessAt: successTimestamp,
                            lastAttemptAt: now,
                            error: null
                        }
                    };
                });
                return true;
            } catch (err) {
                if (currentSessionEpoch.current !== thisSessionEpoch) {
                    return false;
                }
                console.warn(`[ConfigMonitor] Error cargando ${item.label} (${key}):`, err);

                // REGLA FUNDAMENTAL: Conservar caché existente si la consulta falla
                const fallbackCount = await item.countFn();
                const friendlyMsg = getFriendlyErrorMessage(err);

                setItemStates((prev) => {
                    if (currentSessionEpoch.current !== thisSessionEpoch) return prev;
                    return {
                        ...prev,
                        [key]: {
                            ...(prev[key] || {}),
                            status: 'ERROR',
                            recordCount: fallbackCount, // se conservan datos locales previos
                            lastAttemptAt: now,
                            error: friendlyMsg
                        }
                    };
                });
                return false;
            } finally {
                delete inFlightPromises.current[key];
            }
        })();

        inFlightPromises.current[key] = promise;
        return promise;
    }, []);

    // 3️⃣ Sincronizar todas las configuraciones respetando orden y concurrencia segura
    // Evita saturación de sockets (max 3-4 concurrentes por lote)
    const syncAll = useCallback(async (options = {}) => {
        const { forceRefresh = false, sessionEpoch } = options;
        if (isSyncingAllRef.current) return;

        const currentEpoch = sessionEpoch !== undefined ? sessionEpoch : currentSessionEpoch.current;
        isSyncingAllRef.current = true;
        setIsSyncingAll(true);

        try {
            const currentSucursalId = Number(localStorage.getItem('idSucursal'));
            const cachedSucursalId = Number(localStorage.getItem('cachedSucursalId'));

            // Aislamiento de Sucursal: si cambió la sucursal, limpiar IndexedDB previo
            if (cachedSucursalId && currentSucursalId && cachedSucursalId !== currentSucursalId) {
                console.log('🔄 [ConfigMonitor] Sucursal cambiada. Limpiando IndexedDB para aislamiento de datos...');
                try {
                    await db.account.clear();
                    await db.subAccount.clear();
                    await db.functions.clear();
                    localStorage.removeItem('cachedSucursalId');
                } catch (err) {
                    console.warn('Error limpiando IndexedDB por cambio de sucursal:', err);
                }
            }

            if (currentSessionEpoch.current !== currentEpoch) return;

            // FASE 1: Críticos IndexedDB en secuencia controlada (Plan de Cuentas, Subcuentas, Funciones)
            const criticalIndexedDB = CATALOG_REGISTRY.filter((c) => c.isCritical && c.storageType === 'IndexedDB');
            for (const item of criticalIndexedDB) {
                if (currentSessionEpoch.current !== currentEpoch) return;
                await syncItem(item.key, { forceRefresh, sessionEpoch: currentEpoch });
            }

            // FASE 2: Críticos localStorage en lotes de 3 para evitar saturar el navegador
            const criticalLocalStorage = CATALOG_REGISTRY.filter((c) => c.isCritical && c.storageType === 'localStorage');
            const chunkSize = 3;
            for (let i = 0; i < criticalLocalStorage.length; i += chunkSize) {
                if (currentSessionEpoch.current !== currentEpoch) return;
                const chunk = criticalLocalStorage.slice(i, i + chunkSize);
                await Promise.all(chunk.map((item) => syncItem(item.key, { forceRefresh, sessionEpoch: currentEpoch })));
            }

            // FASE 3: Catálogos secundarios en lotes de 4
            const secondary = CATALOG_REGISTRY.filter((c) => !c.isCritical);
            for (let i = 0; i < secondary.length; i += 4) {
                if (currentSessionEpoch.current !== currentEpoch) return;
                const chunk = secondary.slice(i, i + 4);
                await Promise.all(chunk.map((item) => syncItem(item.key, { forceRefresh, sessionEpoch: currentEpoch })));
                // Pequeña pausa entre lotes secundarios
                await new Promise((res) => setTimeout(res, 40));
            }
        } finally {
            isSyncingAllRef.current = false;
            setIsSyncingAll(false);
        }
    }, [syncItem]);

    // 4️⃣ Reintentar únicamente configuraciones fallidas o pendientes
    const retryFailed = useCallback(async () => {
        if (isSyncingAllRef.current) return;

        const failedOrPending = CATALOG_REGISTRY.filter((item) => {
            const state = itemStates[item.key];
            return state && (state.status === 'ERROR' || state.status === 'PENDING');
        });

        if (failedOrPending.length === 0) return;

        isSyncingAllRef.current = true;
        setIsSyncingAll(true);
        const epoch = currentSessionEpoch.current;

        try {
            for (let i = 0; i < failedOrPending.length; i += 3) {
                if (currentSessionEpoch.current !== epoch) return;
                const chunk = failedOrPending.slice(i, i + 3);
                await Promise.all(chunk.map((item) => syncItem(item.key, { forceRefresh: false, retrying: true, sessionEpoch: epoch })));
            }
        } finally {
            isSyncingAllRef.current = false;
            setIsSyncingAll(false);
        }
    }, [itemStates, syncItem]);

    // 5️⃣ Resetear estado en cierre de sesión
    const resetConfigState = useCallback(() => {
        const initial = {};
        CATALOG_REGISTRY.forEach((item) => {
            initial[item.key] = {
                status: 'PENDING',
                recordCount: 0,
                lastSuccessAt: null,
                lastAttemptAt: null,
                error: null
            };
        });
        setItemStates(initial);
        localStorage.removeItem(CONFIG_META_KEY);
    }, []);

    // 6️⃣ Bootstrap reactivo a Session Ready
    // Se ejecuta automáticamente al pasar de no-autenticado a autenticado (Login)
    // o al montar la app con sesión ya existente (F5 o ruta directa).
    // Es 100% IDEMPOTENTE y no depende de ninguna página (vive a nivel de sesión).
    useEffect(() => {
        if (!isSessionReady()) {
            if (!isAuthenticated && !isInitializing) {
                lastBootstrapSessionKey.current = null;
                currentSessionEpoch.current += 1;
                inFlightPromises.current = {};
                resetConfigState();
            }
            return;
        }

        const idUsuario = localStorage.getItem('idUsuario');
        const idRol = localStorage.getItem('idRol');
        const idSucursal = localStorage.getItem('idSucursal');
        const sessionKey = `${idUsuario}_${idRol}_${idSucursal}`;

        // IDEMPOTENCIA: Si ya se inició/ejecutó para esta sesión exacta, no repetir
        if (lastBootstrapSessionKey.current === sessionKey) {
            return;
        }

        lastBootstrapSessionKey.current = sessionKey;
        currentSessionEpoch.current += 1;
        const sessionEpoch = currentSessionEpoch.current;

        let active = true;

        const bootstrap = async () => {
            // Reconciliar estado con lo que ya existe localmente
            await reconcileLocalState();

            if (!active || currentSessionEpoch.current !== sessionEpoch) return;

            // Iniciar sincronización de fondo para lo que falte
            // No forzamos si ya existe caché válido
            await syncAll({ forceRefresh: false, sessionEpoch });
        };

        bootstrap();

        return () => {
            active = false;
        };
    }, [isAuthenticated, isInitializing, isSessionReady, reconcileLocalState, syncAll, resetConfigState]);

    // 7️⃣ Listener dedicado para cambios de rol o sucursal
    useEffect(() => {
        const handleRoleChange = async () => {
            console.log('🔄 [ConfigMonitor] Evento roleChanged recibido. Reiniciando sincronización...');
            lastBootstrapSessionKey.current = null;
            currentSessionEpoch.current += 1;
            inFlightPromises.current = {};
            const sessionEpoch = currentSessionEpoch.current;

            await reconcileLocalState();
            if (currentSessionEpoch.current !== sessionEpoch) return;
            await syncAll({ forceRefresh: true, sessionEpoch });
        };

        window.addEventListener('roleChanged', handleRoleChange);

        return () => {
            window.removeEventListener('roleChanged', handleRoleChange);
        };
    }, [reconcileLocalState, syncAll]);

    // 7️⃣ Resumen de contadores
    const summary = {
        total: CATALOG_REGISTRY.length,
        ready: Object.values(itemStates).filter((s) => s.status === 'READY').length,
        loading: Object.values(itemStates).filter((s) => s.status === 'LOADING').length,
        error: Object.values(itemStates).filter((s) => s.status === 'ERROR').length,
        pending: Object.values(itemStates).filter((s) => s.status === 'PENDING').length
    };

    // 8️⃣ Cálculo del Badge del Header basado en configuraciones CRÍTICAS
    // ROJO: si alguna crítica falló
    // AZUL: si alguna configuración está cargándose
    // WARNING: si alguna crítica está pendiente
    // VERDE: si todas las críticas están READY
    // GRIS: estado inicial / idle
    const criticalItems = CATALOG_REGISTRY.filter((c) => c.isCritical && !(c.skipIf && c.skipIf()));
    const criticalStates = criticalItems.map((c) => itemStates[c.key]?.status || 'PENDING');

    let globalBadge = 'default'; // 'success' | 'info' | 'warning' | 'error' | 'default'
    let globalTooltip = 'Configuración local no iniciada';

    const hasAnyLoading = Object.values(itemStates).some((s) => s.status === 'LOADING') || isSyncingAll;
    const hasCriticalError = criticalStates.some((st) => st === 'ERROR');
    const hasCriticalPending = criticalStates.some((st) => st === 'PENDING');
    const allCriticalReady = criticalStates.every((st) => st === 'READY');

    if (hasCriticalError) {
        globalBadge = 'error';
        globalTooltip = 'Hay configuraciones críticas que no pudieron cargarse';
    } else if (hasAnyLoading) {
        globalBadge = 'info';
        globalTooltip = 'Cargando configuraciones en segundo plano...';
    } else if (hasCriticalPending) {
        globalBadge = 'warning';
        globalTooltip = 'Hay configuraciones pendientes de carga';
    } else if (allCriticalReady) {
        globalBadge = 'success';
        globalTooltip = 'Todas las configuraciones críticas cargadas';
    }

    const value = {
        catalogList: CATALOG_REGISTRY,
        itemStates,
        summary,
        globalBadge,
        globalTooltip,
        isSyncingAll,
        isReconciled,
        syncItem,
        syncAll,
        retryFailed,
        reconcileLocalState,
        resetConfigState
    };

    return <ConfigMonitorContext.Provider value={value}>{children}</ConfigMonitorContext.Provider>;
};

export const useConfigMonitor = () => {
    const context = useContext(ConfigMonitorContext);
    if (!context) {
        throw new Error('useConfigMonitor debe usarse dentro de un ConfigMonitorProvider');
    }
    return context;
};

export default ConfigMonitorContext;
